/******************************************************************
 * ##: Location tracker (singleton)
 * Owns a single navigator.geolocation.watchPosition per active service, applies the
 * 20m / 60s persistence rule to automatic positions, captures action-triggered
 * positions on demand, and manages a Screen Wake Lock while a service is active.
 *
 * Exposed as a factory (createLocationTracker) for testability and a default
 * singleton (locationTracker) that the app must share so only one watcher exists.
 *
 * TL;DR: One GPS watcher per service, stores positions offline.
 * History:
 * 23-07-2026: Created
 ******************************************************************/

import { LOCATION_MAXIMUM_AGE_MS, LOCATION_TIMEOUT_MS, LOCATION_ACTION_FRESHNESS_MS, LOCATION_STATUS } from "constants/location";
import { getLastLocationRecordForService, saveLocationRecord } from "utils/offlineCache";
import { isOnline as defaultIsOnline } from "utils/networkConnection";
import { buildLocationRecord } from "./record";
import { getDeviceId } from "./device";
import { getGeolocationPermissionState, GEO_PERMISSION } from "./permission";
import { shouldPersistPosition } from "./decision";

const WATCH_OPTIONS = {
  enableHighAccuracy: true,
  maximumAge: LOCATION_MAXIMUM_AGE_MS,
  timeout: LOCATION_TIMEOUT_MS,
};

// Default Screen Wake Lock helpers (no-ops where unsupported).
const defaultRequestWakeLock = async () => {
  try {
    if (typeof navigator !== "undefined" && navigator.wakeLock && typeof navigator.wakeLock.request === "function") {
      return await navigator.wakeLock.request("screen");
    }
  } catch (error) {
    // Wake Lock is progressive/optional; ignore failures.
  }
  return null;
};

const defaultReleaseWakeLock = async (sentinel) => {
  try {
    if (sentinel && typeof sentinel.release === "function") {
      await sentinel.release();
    }
  } catch (error) {
    // Ignore release failures.
  }
};

/**
 * Create a location tracker instance. Dependencies are injectable for testing.
 */
export const createLocationTracker = ({
  geolocation = typeof navigator !== "undefined" ? navigator.geolocation : undefined,
  saveRecord = saveLocationRecord,
  getLastRecord = getLastLocationRecordForService,
  requestWakeLock = defaultRequestWakeLock,
  releaseWakeLock = defaultReleaseWakeLock,
  now = () => Date.now(),
  isOnline = defaultIsOnline,
  getPermissionState = getGeolocationPermissionState,
  onError = null,
} = {}) => {
  // Connectivity at record time (true = had internet). Safe if the dep is missing.
  const readIsOnline = () => (typeof isOnline === "function" ? Boolean(isOnline()) : null);
  let watchId = null;
  let activeService = null; // { service_id_raw, equipment_id, user }
  let lastKnownPosition = null; // raw GeolocationPosition
  let status = LOCATION_STATUS.IDLE;
  let wakeLockSentinel = null;
  let persistQueue = Promise.resolve(); // serialize automatic persistence
  let lastStatusPermission = null; // last permission state written as a status record (dedupe)
  const listeners = new Set();

  const setStatus = (nextStatus) => {
    status = nextStatus;
    listeners.forEach((listener) => {
      try {
        listener(status);
      } catch (error) {
        // A listener failure must not break tracking.
      }
    });
  };

  const isSupported = () => Boolean(geolocation && typeof geolocation.watchPosition === "function");

  // Persist an automatic position if it satisfies the 20m / 60s rule.
  const handleAutomaticPosition = async (position) => {
    if (!activeService) return;
    const serviceIdRaw = activeService.service_id_raw;

    const lastResponse = await getLastRecord(serviceIdRaw);
    const lastRecord = lastResponse && lastResponse.status ? lastResponse.data : null;

    const persist = shouldPersistPosition({
      lastRecord,
      candidate: { latitude: position.coords.latitude, longitude: position.coords.longitude },
      now: now(),
    });
    if (!persist) return;

    const record = buildLocationRecord({
      position,
      service_id_raw: serviceIdRaw,
      equipment_id: activeService.equipment_id,
      action: null,
      action_reference: null,
      is_online: readIsOnline(),
      user: activeService.user || null,
      device: getDeviceId(),
      permission_state: GEO_PERMISSION.GRANTED,
    });
    // A fix means the permission is granted; clear any pending problem-state dedupe.
    lastStatusPermission = GEO_PERMISSION.GRANTED;
    await saveRecord(record);
  };

  // Emit a status record (no coordinates) describing the geolocation permission when it is
  // not granted, so the backoffice can alert that the tablet has not accepted (or rejected)
  // location access. Deduped by the last reported state so we do not spam records.
  const captureGeoStatus = async () => {
    try {
      if (!activeService) return;
      const state = await getPermissionState();
      if (state === GEO_PERMISSION.GRANTED) {
        lastStatusPermission = state;
        return;
      }
      if (state === lastStatusPermission) return;
      lastStatusPermission = state;
      const record = buildLocationRecord({
        position: null,
        service_id_raw: activeService.service_id_raw,
        equipment_id: activeService.equipment_id,
        action: null,
        action_reference: null,
        is_online: readIsOnline(),
        user: activeService.user || null,
        device: getDeviceId(),
        permission_state: state,
      });
      await saveRecord(record);
    } catch (error) {
      // Never let a status capture break tracking.
    }
  };

  const onPosition = (position) => {
    lastKnownPosition = position;
    setStatus(LOCATION_STATUS.ACTIVE);
    // Serialize so each position sees the previous save when evaluating the rule.
    persistQueue = persistQueue.then(() => handleAutomaticPosition(position)).catch(() => {});
  };

  const onWatchError = (error) => {
    if (error && error.code === 1) {
      setStatus(LOCATION_STATUS.DENIED);
      captureGeoStatus();
    } else if (error && error.code === 2) {
      setStatus(LOCATION_STATUS.UNAVAILABLE);
      captureGeoStatus();
    } else {
      // Timeout (3) or unknown: keep the watcher alive and keep waiting.
      setStatus(LOCATION_STATUS.WAITING);
    }
    if (typeof onError === "function") {
      try {
        onError(error);
      } catch (callbackError) {
        // Ignore telemetry callback failures.
      }
    }
  };

  const acquireWakeLock = async () => {
    if (wakeLockSentinel) return;
    wakeLockSentinel = await requestWakeLock();
  };

  const dropWakeLock = async () => {
    const sentinel = wakeLockSentinel;
    wakeLockSentinel = null;
    await releaseWakeLock(sentinel);
  };

  // Re-acquire the wake lock when the app returns to the foreground during a service.
  const handleVisibilityChange = () => {
    if (typeof document === "undefined") return;
    if (document.visibilityState === "visible" && watchId !== null) {
      acquireWakeLock();
    }
  };

  const startTracking = ({ service_id_raw, equipment_id = null, user = null } = {}) => {
    if (!service_id_raw) {
      return { status: false, data: "service_id_raw is required" };
    }
    if (!isSupported()) {
      setStatus(LOCATION_STATUS.UNAVAILABLE);
      return { status: false, data: "Geolocation is not available" };
    }

    // Single-watcher guard: already tracking the same service → no-op.
    if (watchId !== null && activeService && String(activeService.service_id_raw) === String(service_id_raw)) {
      return { status: true, data: "Already tracking" };
    }

    // Tracking a different service → stop the previous watcher first.
    if (watchId !== null) {
      stopTracking();
    }

    activeService = { service_id_raw, equipment_id, user };
    lastStatusPermission = null;
    setStatus(LOCATION_STATUS.WAITING);
    watchId = geolocation.watchPosition(onPosition, onWatchError, WATCH_OPTIONS);

    // Record the initial permission state (e.g. "prompt" not yet accepted, or "denied").
    captureGeoStatus();
    acquireWakeLock();
    if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }

    return { status: true, data: "Tracking started" };
  };

  function stopTracking() {
    if (watchId !== null && geolocation && typeof geolocation.clearWatch === "function") {
      geolocation.clearWatch(watchId);
    }
    watchId = null;
    activeService = null;
    setStatus(LOCATION_STATUS.IDLE);
    if (typeof document !== "undefined" && typeof document.removeEventListener === "function") {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    }
    dropWakeLock();
    return { status: true, data: "Tracking stopped" };
  }

  // Promisified getCurrentPosition that never rejects (resolves null on failure/timeout).
  const getCurrentPositionSafe = (timeoutMs = LOCATION_TIMEOUT_MS) =>
    new Promise((resolve) => {
      if (!geolocation || typeof geolocation.getCurrentPosition !== "function") {
        resolve(null);
        return;
      }
      let settled = false;
      const finish = (value) => {
        if (settled) return;
        settled = true;
        resolve(value);
      };
      // Outer guard in case the platform ignores the options.timeout.
      const guard = setTimeout(() => finish(null), timeoutMs + 1000);
      geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(guard);
          finish(position);
        },
        () => {
          clearTimeout(guard);
          finish(null);
        },
        { ...WATCH_OPTIONS, timeout: timeoutMs },
      );
    });

  /**
   * Capture a position for an action. Always creates a new record with action /
   * action_reference and never throws; a GPS failure must not block the action.
   * Reuses the last known fix if it is fresh enough; otherwise requests a new one.
   */
  const captureActionLocation = async ({ service_id_raw, equipment_id = null, action, action_reference = null, user = null } = {}) => {
    try {
      if (!service_id_raw || !action) {
        return { status: false, data: "service_id_raw and action are required" };
      }

      let position = null;
      if (lastKnownPosition && lastKnownPosition.timestamp && now() - lastKnownPosition.timestamp <= LOCATION_ACTION_FRESHNESS_MS) {
        position = lastKnownPosition;
      } else {
        position = await getCurrentPositionSafe();
      }

      if (!position) {
        // No fix available: do not block the operational action.
        return { status: false, data: "No position available" };
      }

      lastKnownPosition = position;
      const record = buildLocationRecord({
        position,
        service_id_raw,
        equipment_id,
        action,
        action_reference,
        is_online: readIsOnline(),
        user: user || (activeService ? activeService.user : null),
        device: getDeviceId(),
        permission_state: GEO_PERMISSION.GRANTED,
      });
      const saveResponse = await saveRecord(record);
      return { status: saveResponse ? saveResponse.status : true, data: record };
    } catch (error) {
      console.error("captureActionLocation error:", error);
      return { status: false, data: error };
    }
  };

  const subscribe = (listener) => {
    if (typeof listener !== "function") return () => {};
    listeners.add(listener);
    // Emit current status immediately.
    try {
      listener(status);
    } catch (error) {
      // Ignore.
    }
    return () => listeners.delete(listener);
  };

  return {
    startTracking,
    stopTracking,
    captureActionLocation,
    subscribe,
    getStatus: () => status,
    isTracking: () => watchId !== null,
    getActiveService: () => activeService,
  };
};

// Shared singleton used across the app so only one watcher exists at a time.
export const locationTracker = createLocationTracker();
